<template>
  <BreakdownSectionComponent
    v-if="Object.keys(regions).length > 0"
    title="Regions"
    wide
  >
    <RegionBreakdownTable
      :regionBreakdown="regions"
      @view-details="(region: string) => $emit('view-details', regions[region])"
    />
  </BreakdownSectionComponent>
  <BreakdownSectionComponent
    v-if="priceEntries.length > 0"
    title="Price breakdown"
    :entries="priceEntries"
    :total
  />
</template>

<script setup lang="ts">
import { computed } from "vue";

import BreakdownSectionComponent, {
  type BreakdownEntry,
} from "@/inventory_selection/BreakdownSectionComponent.vue";
import type { ItemMwh } from "@/inventory_selection/inventorySelectionTypes";
import type { FlattenedPortfolioItem } from "@/types";

import RegionBreakdownTable from "./RegionBreakdownTable.vue";

/** One region's row in the regions card, with what the details modal lists for it. */
export interface RegionBreakdownEntry {
  region: string;
  total_mwh: number;
  company_count: number;
  companies: { id: number; name: string; country: string }[];
  portfolio_items: {
    portfolio_item_id: number;
    certificate_id: number;
    count: number;
    production_month: string | null;
    technology_code: string;
    company_id: number;
  }[];
}

/**
 * The admin-only breakdown cards of a sale: regions with their producers, and the price bands
 * (spot vs each minimum price). Rendered inside the shared selection panel's breakdown grid.
 *
 * `regionBreakdown` replaces the regions derived from `items` — a saved transaction passes the
 * backend's issuing-registry breakdown.
 */
const props = defineProps<{
  items: FlattenedPortfolioItem[];
  getMwh: ItemMwh;
  /** The MWh of `items`, as the selection panel's `breakdowns` slot gives it. */
  total: number;
  regionBreakdown?: Record<string, RegionBreakdownEntry>;
}>();

defineEmits<{
  "view-details": [region: RegionBreakdownEntry];
}>();

const regions = computed(
  (): Record<string, RegionBreakdownEntry> =>
    props.regionBreakdown ?? regionsFromItems(props.items)
);

// Spot rows sell at any price; minimum-price rows are grouped by their floor. A floor is the
// producer's own, set in EUR/MWh whatever currency the deal is in, so it is labelled EUR.
const priceEntries = computed((): BreakdownEntry[] => {
  let spotMwh = 0;
  let otherMwh = 0;
  const minPriceGroups: Record<string, number> = {};
  for (const item of props.items) {
    const mwh = props.getMwh(item);
    if (
      item.portfolio_type === "SPOT_MARKET_PRICE" ||
      item.portfolio_type === "SPOT_STX"
    ) {
      spotMwh += mwh;
    } else if (item.portfolio_type === "SPOT_MIN_PRICE") {
      const price = item.limit_price ?? "N/A";
      minPriceGroups[price] = (minPriceGroups[price] ?? 0) + mwh;
    } else {
      otherMwh += mwh;
    }
  }

  return [
    ...(spotMwh > 0
      ? [{ label: "Spot Market / STX · no minimum", mwh: spotMwh }]
      : []),
    ...Object.entries(minPriceGroups)
      .sort()
      .map(([price, mwh]) => ({
        label:
          price === "N/A"
            ? "Spot Min Price · N/A"
            : `Spot Min Price · ≥ ${price} EUR/MWh`,
        mwh,
      })),
    ...(otherMwh > 0
      ? [{ label: "Other (Forward, etc.)", mwh: otherMwh }]
      : []),
  ];
});

function regionsFromItems(
  items: FlattenedPortfolioItem[]
): Record<string, RegionBreakdownEntry> {
  const breakdown: Record<string, RegionBreakdownEntry> = {};
  for (const item of items) {
    const region = item.issuing_region || "Unknown";
    const mwh = props.getMwh(item);
    const entry = (breakdown[region] ??= {
      region,
      total_mwh: 0,
      company_count: 0,
      companies: [],
      portfolio_items: [],
    });
    entry.total_mwh += mwh;
    if (!entry.companies.some((c) => c.id === item.company_id)) {
      entry.companies.push({
        id: item.company_id,
        name: item.company_name,
        country: region,
      });
      entry.company_count = entry.companies.length;
    }
    entry.portfolio_items.push({
      portfolio_item_id: item.id,
      certificate_id: item.certificate_id,
      count: mwh,
      production_month: item.production_month,
      technology_code: item.technology_code ?? "",
      company_id: item.company_id,
    });
  }
  return breakdown;
}
</script>
